import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isSchemaError, clearSupabaseSession } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        if (error?.code === 'PGRST116') {
          return null;
        }
        console.log('Error fetching profile:', error?.message);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Error in fetchUserProfile:', err);
      return null;
    }
  };

  const isRefreshTokenError = (err) => {
    if (!err) return false;
    const msg = err?.message?.toLowerCase() || '';
    const name = err?.name?.toLowerCase() || '';
    return (msg?.includes('refresh token') ||
    msg?.includes('invalid refresh') ||
    msg?.includes('refresh_token_not_found') ||
    msg?.includes('token not found') ||
    name === 'authapiError' ||
    err?.status === 400 || err?.code === 'refresh_token_not_found');
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Use getUser() for server-side validation — getSession() only reads localStorage
        // and can return stale/expired sessions that bypass ProtectedRoute
        const { data: userData, error: userError } = await supabase?.auth?.getUser();

        if (userError) {
          // Handle invalid/expired refresh token — clear stale session silently
          if (isRefreshTokenError(userError)) {
            clearSupabaseSession();
            await supabase?.auth?.signOut()?.catch(() => {});
          }
          setUser(null);
          setUserProfile(null);
        } else if (!userData?.user) {
          // No valid server-confirmed session — user must log in
          setUser(null);
          setUserProfile(null);
        } else {
          setUser(userData?.user);
          const profile = await fetchUserProfile(userData?.user?.id);
          setUserProfile(profile);
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        // If it's a refresh token error, clear the session
        if (isRefreshTokenError(err)) {
          clearSupabaseSession();
          try { await supabase?.auth?.signOut(); } catch (_) {}
        }
        setError(err?.message);
        setUser(null);
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    const authListener = supabase?.auth?.onAuthStateChange(
      async function(event, session) {
        // Skip INITIAL_SESSION — handled by initializeAuth above
        if (event === 'INITIAL_SESSION') return;

        // Handle token refresh failure — clear stale session and force re-login
        if (event === 'TOKEN_REFRESHED' && !session) {
          clearSupabaseSession();
          await supabase?.auth?.signOut()?.catch(() => {});
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          return;
        }

        if (event === 'SIGNED_OUT') {
          clearSupabaseSession();
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          return;
        }

        if (session && session?.user) {
          setUser(session?.user);
          // Fetch profile in background — do NOT set loading=true here
          // This prevents ProtectedRoute from showing infinite spinner
          fetchUserProfile(session?.user?.id)?.then((profile) => {
            setUserProfile(profile);
          })?.catch(() => {
            // Profile fetch failure is non-blocking
          });
        } else {
          setUser(null);
          setUserProfile(null);
          setLoading(false);
        }
      }
    );

    return function() {
      if (authListener && authListener?.data && authListener?.data?.subscription) {
        authListener?.data?.subscription?.unsubscribe();
      }
    };
  }, []);

  const signUp = async (email, password, metadata) => {
    const meta = metadata || {};
    try {
      setError(null);
      const { data, error } = await supabase?.auth?.signUp({
        email: email,
        password: password,
        options: {
          data: meta
        }
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during signup:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return { data: data, error: null };
    } catch (err) {
      setError(err?.message);
      throw err;
    }
  };

  const signIn = async (email, password) => {
    try {
      setError(null);
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: email,
        password: password
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during signin:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return { data: data, error: null };
    } catch (err) {
      setError(err?.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      const { error } = await supabase?.auth?.signOut();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during signout:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      setUser(null);
      setUserProfile(null);
    } catch (err) {
      setError(err?.message);
      throw err;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      const origin = window.location ? window.location?.origin : '';
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: origin + '/reset-password'
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during password reset:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return { data: data, error: null };
    } catch (err) {
      setError(err?.message);
      throw err;
    }
  };

  const updateUserMetadata = async (metadata) => {
    try {
      setError(null);
      const { data, error } = await supabase?.auth?.updateUser({
        data: metadata
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error updating metadata:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return { data: data, error: null };
    } catch (err) {
      setError(err?.message);
      throw err;
    }
  };

  const updateUserProfile = async (updates) => {
    try {
      setError(null);
      if (!user) throw new Error('No user logged in');

      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error updating profile:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      setUserProfile(data);
      return { data: data, error: null };
    } catch (err) {
      setError(err?.message);
      throw err;
    }
  };

  const signInWithOAuth = async (provider) => {
    try {
      setError(null);
      const origin = window.location ? window.location?.origin : '';
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: origin + '/dashboard'
        }
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during OAuth signin:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return { data: data, error: null };
    } catch (err) {
      setError(err?.message);
      throw err;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUserMetadata,
    updateUserProfile,
    signInWithOAuth,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
